<script setup>
import { computed } from 'vue';
import { Doughnut } from 'vue-chartjs';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';

const props = defineProps({
  data: {
    type: Array,
    required: true,
    // Formato: [{ label, value, color }]
  },
  centerLabel: {
    type: String,
    default: '',
  },
  centerValue: {
    type: [String, Number],
    default: '',
  },
  height: {
    type: String,
    default: '250px',
  },
  showLegend: {
    type: Boolean,
    default: true,
  },
  legendPosition: {
    type: String,
    default: 'right', // 'top', 'bottom', 'left', 'right'
  },
  cutout: {
    type: String,
    default: '70%',
  },
  formatValue: {
    type: Function,
    default: value => value?.toLocaleString() ?? '--',
  },
});

ChartJS.register(ArcElement, Tooltip, Legend);

const fontFamily =
  'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

const total = computed(() =>
  props.data.reduce((sum, item) => sum + (item.value || 0), 0)
);

const chartData = computed(() => ({
  labels: props.data.map(item => item.label),
  datasets: [
    {
      data: props.data.map(item => item.value),
      backgroundColor: props.data.map(item => item.color),
      borderWidth: 0,
      hoverOffset: 4,
      borderRadius: 2,
    },
  ],
}));

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  cutout: props.cutout,
  plugins: {
    legend: {
      display: props.showLegend,
      position: props.legendPosition,
      labels: {
        usePointStyle: true,
        pointStyle: 'circle',
        padding: 16,
        font: {
          family: fontFamily,
          size: 12,
        },
        color: '#64748b',
        generateLabels: chart => {
          const data = chart.data;
          if (data.labels.length && data.datasets.length) {
            return data.labels.map((label, i) => {
              const value = data.datasets[0].data[i];
              const percentage =
                total.value > 0 ? ((value / total.value) * 100).toFixed(1) : 0;
              return {
                text: `${label} (${percentage}%)`,
                fillStyle: data.datasets[0].backgroundColor[i],
                hidden: false,
                index: i,
              };
            });
          }
          return [];
        },
      },
    },
    tooltip: {
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      titleFont: {
        family: fontFamily,
        size: 12,
        weight: '500',
      },
      bodyFont: {
        family: fontFamily,
        size: 11,
      },
      padding: 10,
      cornerRadius: 8,
      displayColors: false,
      callbacks: {
        label: context => {
          const value = context.parsed;
          const percentage =
            total.value > 0 ? ((value / total.value) * 100).toFixed(1) : 0;
          return `${context.label}: ${props.formatValue(value)} (${percentage}%)`;
        },
      },
    },
  },
}));

const displayCenterValue = computed(() => {
  if (props.centerValue !== '') return props.centerValue;
  return props.formatValue(total.value);
});
</script>

<template>
  <div :style="{ height }" class="relative">
    <Doughnut :data="chartData" :options="chartOptions" />

    <!-- Centro do Donut -->
    <div
      v-if="centerLabel || centerValue !== ''"
      class="absolute inset-0 flex flex-col items-center justify-center pointer-events-none"
      :style="{
        paddingRight: showLegend && legendPosition === 'right' ? '30%' : '0',
      }"
    >
      <span
        v-if="centerLabel"
        class="text-xs font-medium text-n-slate-10 uppercase tracking-wide"
      >
        {{ centerLabel }}
      </span>
      <span class="text-xl font-semibold text-n-slate-12 mt-0.5">
        {{ displayCenterValue }}
      </span>
    </div>
  </div>
</template>
